import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SocketService } from './socket.service';
import { environment } from '../../../environments/environment';

/** A live dispatch alert shown in the notification bell dropdown. */
export interface DispatchAlert {
  id: string;          // tripId, or 'chat:<conversationId>' for chat entries (dedup key)
  tripNumber: string;
  titleKey: string;    // i18n key for the headline (translated in the bell + push)
  detail: string;      // patient / time line
  kind: 'new' | 'unassigned' | 'assigned' | 'chat';
  at: number;          // received timestamp
  severity: 'info' | 'alert'; // 'alert' → red bell + browser push
  count?: number;      // >1 → this is a combined "N new immediate bookings" banner
  /** Router path to open on click. Bookings → /trips (default); chat → /chat/:id. */
  route?: string;
  /** Pre-translated title (chat titles are dynamic names, not i18n keys). */
  titleText?: string;
}

/** Live chat notification pushed to the per-user room (WP 1211). */
export interface ChatNotify {
  conversationId: string;
  contextType: string;
  channelTitle: string;   // DM → sender's name; team/thread → channel title
  senderName: string;
  preview: string;
  at: string;
}

/**
 * Real-time dispatch notifications over socket.io. Connects to the shared
 * `dispatch` room and turns server events into bell alerts. The bell goes RED
 * when any unread alert is a severity 'alert' (a new "now" booking, or a new
 * unassigned booking) — matching the ticket's requirement.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private wired = false;
  private readonly _alerts = new BehaviorSubject<DispatchAlert[]>([]);
  readonly alerts$ = this._alerts.asObservable();
  private readonly _unread = new BehaviorSubject<number>(0);
  readonly unread$ = this._unread.asObservable();
  /** Total unread CHAT messages across all conversations — drives the Messages
   *  sidebar badge. Seeded from GET /conversations, bumped live per chat ping. */
  private readonly _chatUnread = new BehaviorSubject<number>(0);
  readonly chatUnread$ = this._chatUnread.asObservable();
  /** Seed the chat-unread total (called by the shell after loading the inbox). */
  setChatUnread(n: number): void { this._chatUnread.next(Math.max(0, n)); }
  /** Whether any UNREAD alert-severity notification is pending (drives the red
   *  bell). Cleared by markAllRead so the bell calms once the dispatcher looks. */
  private hasUnreadAlert = false;
  /** Live list of active banner alerts, owned by the service so it survives the
   *  banner component's lifecycle (the component only renders + dismisses). */
  private readonly _banners = new BehaviorSubject<DispatchAlert[]>([]);
  readonly banners$ = this._banners.asObservable();
  private bannerTimers = new Map<string, any>();
  /** Synthetic id for the single combined "N new immediate bookings" banner used
   *  when a burst of immediate bookings arrives together. */
  private static readonly BURST_ID = 'immediate-burst';
  /** Immediate alerts collected in the current burst window, newest first. */
  private burst: DispatchAlert[] = [];

  /** Show an immediate booking as a banner. A single arrival shows its own card;
   *  when more immediate bookings are already banner-active, they collapse into
   *  ONE "N new immediate bookings" banner instead of stacking separate cards. */
  private addBanner(a: DispatchAlert): void {
    // Track this immediate alert in the burst (dedup by trip id, cap the memory).
    this.burst = [a, ...this.burst.filter(x => x.id !== a.id)].slice(0, 50);

    if (this.burst.length === 1) {
      // First of a (possible) burst → show its own card.
      this.setBanner(a);
    } else {
      // Second+ within the window → collapse everything into one combined banner.
      const n = this.burst.length;
      this.setBanner({
        id: NotificationService.BURST_ID,
        tripNumber: a.tripNumber,               // newest, for a fallback open
        titleKey: 'notif.new_immediate_many',   // "{{count}} new immediate bookings"
        detail: '',
        kind: 'new', at: Date.now(), severity: 'alert',
        count: n,
      });
    }
  }

  /** Replace/refresh a single banner slot (there is at most one immediate banner
   *  at a time now — either a single card or the combined burst card). */
  private setBanner(a: DispatchAlert): void {
    // Only ever keep the current immediate banner; a combined banner supersedes
    // the single card it grew out of.
    this._banners.next([a]);
    this.bannerTimers.forEach(t => clearTimeout(t));
    this.bannerTimers.clear();
    this.bannerTimers.set(a.id, setTimeout(() => this.dismissBanner(a.id), 12_000));
  }
  dismissBanner(id: string): void {
    clearTimeout(this.bannerTimers.get(id));
    this.bannerTimers.delete(id);
    this._banners.next(this._banners.value.filter(x => x.id !== id));
    // Dismissing the banner ends the current burst grouping.
    this.burst = [];
  }
  clearBanners(): void {
    this.bannerTimers.forEach(t => clearTimeout(t));
    this.bannerTimers.clear();
    this._banners.next([]);
    this.burst = [];
  }
  /** Emits a tripNumber to open+highlight (from an OS-notification or bell click).
   *  The shell subscribes and routes to /trips?highlight=<tripNumber>. */
  readonly navigate$ = new Subject<string>();
  /** Emits a conversation id to open (chat notification click). Shell routes to /chat/:id. */
  readonly navigateChat$ = new Subject<string>();

  /**
   * Every dispatch event, raw, for views that keep themselves up to date.
   *
   * Separate from the bell on purpose: this also carries status churn and
   * deletions, which must never notify — a driver walking a job through eight
   * statuses would otherwise chime eight times. Exposed as a stream rather than
   * letting each view call socket.on(), because SocketService has no way to
   * detach a handler: a component registering on every navigation would stack
   * up listeners that all keep firing.
   */
  readonly dispatch$ = new Subject<{ type: string; tripId: string; tripNumber: string }>();

  constructor(private zone: NgZone, private translate: TranslateService, private socket: SocketService) {}

  /** Id of the conversation the user currently has open (set by the chat page),
   *  so we don't push/chime for a thread they're already looking at. */
  private openConversationId: string | null = null;
  setOpenConversation(id: string | null): void { this.openConversationId = id; }

  private userId?: string;

  /** Connect once (call from the shell after login). Safe to call repeatedly.
   *  Pass the current user id so chat notifications route to the per-user room. */
  connect(userId?: string): void {
    if (userId) this.userId = userId;
    if (this.wired) return;
    this.wired = true;
    this.requestPushPermission();
    // Re-join the dispatch room on every (re)connect: a reconnect is a fresh
    // server session with no rooms. The per-user room is joined server-side
    // from the authenticated identity, so no id is sent for it.
    this.socket.onConnect(() => this.socket.emit('join:dispatch'));
    this.socket.on('dispatch', (evt: any) => { this.handle(evt); this.dispatch$.next(evt); });
    // Chat message pings for channels the user doesn't have open (WP 1211).
    this.socket.on('chat:notify', (evt: ChatNotify) => this.handleChat(evt));
  }

  /** Ask once for browser-push permission (used for high-alert immediate jobs). */
  requestPushPermission(): void {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') Notification.requestPermission().catch(() => {});
    this.registerNotifWorker();
  }

  /** Active notification service-worker registration, once ready. */
  private swReg: any = null;
  private swRegistering = false;

  /** Register the app service worker so desktop pushes are shown via the SW (no
   *  browser origin line), and route SW notification clicks. NB: this must be the
   *  same script main.ts registers (mobimed-sw.js = notifications + Angular PWA
   *  runtime) — registering a different script here would replace that
   *  registration and silently kill offline/update support. */
  private registerNotifWorker(): void {
    const sw = (navigator as any).serviceWorker;
    if (!sw?.register || this.swRegistering || this.swReg) return;
    this.swRegistering = true;
    sw.register('/mobimed-sw.js')
      .then((reg: any) => { this.swReg = reg; })
      .catch(() => { /* pushes fall back to the plain path */ })
      .finally(() => { this.swRegistering = false; });
    sw.addEventListener('message', (e: MessageEvent) => {
      const msg = e.data;
      if (msg?.type !== 'notif-click') return;
      this.zone.run(() => {
        if (msg.data?.chat) this.navigateChat$.next(msg.data.chat);
        else if (msg.data?.tripNumber) this.navigate$.next(msg.data.tripNumber);
      });
    });
  }

  /** Resolve an *active* SW registration to show a notification through (so no
   *  browser origin line appears). Registers on demand and waits for activation,
   *  so it works even on the first push after a fresh load. */
  private async swRegistration(): Promise<any | null> {
    const sw = (navigator as any).serviceWorker;
    if (!sw?.register) return null;
    try {
      let reg = this.swReg
        || (await sw.getRegistration('/mobimed-sw.js'))
        || (await sw.register('/mobimed-sw.js'));
      // Wait for an active worker (install→activate can lag the first load).
      if (reg && !reg.active) {
        await new Promise<void>((resolve) => {
          const check = () => { if (reg.active) resolve(); };
          const installing = reg.installing || reg.waiting;
          if (installing) installing.addEventListener('statechange', check);
          check();
          setTimeout(resolve, 1500); // don't hang a notification forever
        });
      }
      this.swReg = reg;
      return reg?.active ? reg : null;
    } catch { return null; }
  }

  /** Show a desktop notification **through the ServiceWorker only**, so the
   *  browser never appends its origin line ("localhost:4213"/domain). If the SW
   *  isn't available, we deliberately skip the OS popup — the in-app bell +
   *  banner + chime still alert the user — rather than fall back to a plain
   *  Notification, which would show the origin line. `onClick` handled by the SW. */
  private async showDesktop(
    title: string,
    opts: NotificationOptions & { data?: any },
    _onClick: () => void,
  ): Promise<void> {
    if (!this.pushGranted) return;
    const reg = await this.swRegistration();
    if (!reg?.showNotification) return; // no SW → no origin-line popup; bell covers it
    try { await reg.showNotification(title, opts); } catch { /* bell still shows it */ }
  }

  get pushGranted(): boolean {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted';
  }

  /** Fire an OS desktop notification for a high-alert (immediate) booking, when
   *  permission is granted. Uses the app icon, vibrates, and stays until dismissed.
   *  Prefers the ServiceWorker path (supports action buttons) and falls back to
   *  the plain Notification constructor. Normal bookings never push. */
  private firePush(a: DispatchAlert): void {
    if (!this.pushGranted || a.severity !== 'alert') return;
    const title = `🚑 ${this.translate.instant(a.titleKey)}`;
    const opts: NotificationOptions & { vibrate?: number[]; renotify?: boolean; actions?: any[]; data?: any } = {
      body: `${a.detail} · ${a.tripNumber}`,
      icon: '/favicon-32.png',
      badge: '/favicon-32.png',
      tag: a.id,               // dedup: same trip replaces an earlier push
      renotify: true,          // re-alert even if a prior push shared the tag
      requireInteraction: true, // stays on screen until the dispatcher acts
      vibrate: [200, 100, 200],
      data: { tripId: a.id, tripNumber: a.tripNumber },
    };
    // Shared SW-preferred path → no browser origin line. Click → highlight booking.
    void this.showDesktop(title, opts, () => this.navigate$.next(a.tripNumber));
  }

  private chimeCooldown = false;
  /** Play the chime at most once per short window, so a burst of simultaneous
   *  immediate bookings sounds one alert instead of overlapping chimes. */
  private playChimeDebounced(): void {
    if (this.chimeCooldown) return;
    this.chimeCooldown = true;
    this.playChime();
    setTimeout(() => { this.chimeCooldown = false; }, 1500);
  }

  /** Short two-tone chime via Web Audio (no asset file). Best-effort: browsers
   *  block audio until the user has interacted with the page at least once. */
  private playChime(): void {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      [ [880, 0], [1174, 0.16] ].forEach(([freq, t]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq as number;
        gain.gain.setValueAtTime(0.0001, now + (t as number));
        gain.gain.exponentialRampToValueAtTime(0.25, now + (t as number) + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + (t as number) + 0.28);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + (t as number));
        osc.stop(now + (t as number) + 0.3);
      });
      setTimeout(() => ctx.close().catch(() => {}), 800);
    } catch { /* audio unavailable */ }
  }

  disconnect(): void { this.socket.disconnect(); this.wired = false; }

  /** Mark all alerts read (badge → 0, bell → neutral). Keeps the list for review. */
  markAllRead(): void { this._unread.next(0); this.hasUnreadAlert = false; }

  clear(): void { this._alerts.next([]); this._unread.next(0); this.hasUnreadAlert = false; }

  private handle(evt: { type: string; tripId: string; tripNumber: string; scheduledAt?: string; patientName?: string; assigned?: boolean; immediate?: boolean }): void {
    if (!evt || !evt.tripId) return;
    if (evt.type === 'trip.created') {
      const sched = evt.scheduledAt ? new Date(evt.scheduledAt) : null;
      const time = sched ? sched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
      // Two tiers (per requirement): an immediate ("Sofort") booking is a HIGH
      // ALERT → red bell + browser push. Every other new booking is a NORMAL
      // notification → bell only, no push.
      const severity: DispatchAlert['severity'] = evt.immediate ? 'alert' : 'info';
      this.push({
        id: evt.tripId, tripNumber: evt.tripNumber,
        titleKey: evt.immediate ? 'notif.new_immediate' : (evt.assigned ? 'notif.new_booking' : 'notif.new_unassigned'),
        detail: `${evt.patientName ?? ''}${time ? ' · ' + time : ''}`.trim(),
        kind: evt.assigned ? 'new' : 'unassigned', at: Date.now(), severity,
      });
    } else if (evt.type === 'trip.assigned') {
      this.push({
        id: evt.tripId, tripNumber: evt.tripNumber,
        titleKey: 'notif.assigned', detail: evt.tripNumber,
        kind: 'assigned', at: Date.now(), severity: 'info',
      });
    }
  }

  /** A chat message arrived for a channel this user isn't currently viewing
   *  (WP 1211). Adds a bell entry + desktop push + chime, unless the thread is
   *  open. Muted channels are filtered out server-side, so anything reaching
   *  here is meant to alert. */
  private handleChat(evt: ChatNotify): void {
    if (!evt?.conversationId) return;
    if (evt.conversationId === this.openConversationId) return; // already looking at it

    const alert: DispatchAlert = {
      id: `chat:${evt.conversationId}`,        // dedup by conversation
      tripNumber: '',
      titleKey: 'notif.chat_message',
      titleText: evt.channelTitle,             // sender name (DM) or channel title
      detail: `${evt.senderName}: ${evt.preview}`,
      kind: 'chat', at: Date.now(), severity: 'info',
      route: `/chat/${evt.conversationId}`,
    };
    // Bell entry (dedup + unread bump for a genuinely-new conversation ping).
    const isNew = !this._alerts.value.some(x => x.id === alert.id);
    this._alerts.next([alert, ...this._alerts.value.filter(x => x.id !== alert.id)].slice(0, 30));
    this._unread.next(this._unread.value + 1);
    // Messages-nav badge: every ping is one more unread chat message.
    this._chatUnread.next(this._chatUnread.value + 1);
    void isNew;
    // Desktop push + chime (chime debounced so a busy channel doesn't spam).
    this.chatPush(alert);
    this.playChimeDebounced();
  }

  /** Remove a chat conversation's bell entry (called when the user opens it). */
  clearChat(conversationId: string): void {
    const id = `chat:${conversationId}`;
    if (!this._alerts.value.some(x => x.id === id)) return;
    this._alerts.next(this._alerts.value.filter(x => x.id !== id));
  }

  /** OS desktop notification for a chat message; click routes to /chat/:id.
   *  Uses the shared SW-preferred path so no browser origin line is shown. */
  private chatPush(a: DispatchAlert): void {
    const conversationId = a.id.replace(/^chat:/, '');
    const title = `💬 ${a.titleText ?? this.translate.instant(a.titleKey)}`;
    const opts: NotificationOptions & { renotify?: boolean; data?: any } = {
      body: a.detail, icon: '/favicon-32.png', badge: '/favicon-32.png',
      tag: a.id, renotify: true, data: { chat: conversationId },
    };
    void this.showDesktop(title, opts, () => this.navigateChat$.next(conversationId));
  }

  private push(a: DispatchAlert): void {
    // Newest first, dedup by trip id, cap the list.
    const isNew = !this._alerts.value.some(x => x.id === a.id);
    const next = [a, ...this._alerts.value.filter(x => x.id !== a.id)].slice(0, 30);
    this._alerts.next(next);
    // Only count a genuinely-new trip as unread — re-firing the same event
    // (reconnect/duplicate socket) must not inflate the badge.
    if (isNew) this._unread.next(this._unread.value + 1);
    if (a.severity === 'alert') this.hasUnreadAlert = true;
    if (a.severity === 'alert') {
      // High alert (immediate booking): OS desktop push (tray when focused) +
      // a fancy in-app banner + a chime, so it can't be missed. The banner list
      // lives in the service so it survives the banner component's lifecycle.
      this.firePush(a);
      this.playChimeDebounced();
      this.addBanner(a);
    }
  }

  /** True when an UNREAD alert-severity notification is pending (drives the red
   *  bell). Once the dispatcher opens the bell (markAllRead) it calms to neutral,
   *  even though the alerts stay in the list for review. */
  hasAlert(): boolean { return this.hasUnreadAlert; }
}
